import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useAuth } from '@/contexts/AuthContext';
import mmrService, { LeaderboardItem, MMRLeaderboardResponseData } from '@/services/mmrService';

interface MMRLeaderboardCardProps {
  refreshKey?: number;
}

interface PositionFilter {
  id: string;
  label: string;
  icon: string;
}

const POSITION_FILTERS: PositionFilter[] = [
  { id: '', label: 'Semua', icon: 'ph:users-bold' },
  { id: 'Goalkeeper', label: 'Kiper', icon: 'ph:hand-palm-bold' },
  { id: 'Defender', label: 'Defender', icon: 'ph:shield-bold' },
  { id: 'Midfielder', label: 'Midfielder', icon: 'ph:arrows-left-right-bold' },
  { id: 'Forward', label: 'Forward', icon: 'ph:soccer-ball-bold' },
];

const AVATAR_BG_COLORS = [
  'bg-[#9E3939]',
  'bg-[#4D5E79]',
  'bg-[#227B7B]',
  'bg-[#3B6DB0]',
  'bg-[#DE6C20]',
  'bg-[#6B46C1]',
  'bg-[#0D9488]',
  'bg-[#B45309]',
];

const getAvatarBg = (name: string, isCurrent?: boolean) => {
  if (isCurrent) return 'bg-[#FC480D]';
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % AVATAR_BG_COLORS.length;
  return AVATAR_BG_COLORS[index];
};

const getInitials = (name: string): string => {
  if (!name) return 'P';
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const getPositionIcon = (position?: string) => {
  const pos = (position || '').toLowerCase();
  if (pos.includes('forward') || pos.includes('penyerang')) return 'ph:soccer-ball-bold';
  if (pos.includes('midfield') || pos.includes('gelandang')) return 'ph:arrows-left-right-bold';
  if (pos.includes('defend') || pos.includes('bek') || pos.includes('bertahan')) return 'ph:shield-bold';
  if (pos.includes('goal') || pos.includes('keeper') || pos.includes('kiper') || pos.includes('gk')) return 'ph:hand-palm-bold';
  return 'ph:shield-bold';
};

export const MMRLeaderboardCard: React.FC<MMRLeaderboardCardProps> = ({ refreshKey }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [selectedPosition, setSelectedPosition] = useState<string>('');
  const [leaderboardData, setLeaderboardData] = useState<MMRLeaderboardResponseData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);

  const fetchLeaderboard = useCallback(async (pos: string) => {
    try {
      setIsLoading(true);
      setHasError(false);
      const res = await mmrService.getLeaderboard(pos, 'invasion', { public: !isAuthenticated });
      if (res.success && res.data) {
        setLeaderboardData(res.data);
      } else {
        setHasError(true);
      }
    } catch (err) {
      console.error('Failed to load MMR leaderboard:', err);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchLeaderboard(selectedPosition);
  }, [selectedPosition, refreshKey, fetchLeaderboard]);

  const rankings = leaderboardData?.rankings || [];
  const currentUser = leaderboardData?.currentUser;

  // Take top 5 rankings
  const top5 = rankings.slice(0, 5);

  // Check if current user is already inside top 5
  const isCurrentUserInTop5 = currentUser
    ? top5.some((p) => p.isCurrentUser || p.userId === currentUser.userId)
    : false;

  // Only display current user at bottom if user exists, has a valid rank, and is not already in top 5
  const showSeparateCurrentUser =
    currentUser && currentUser.rank > 0 && !isCurrentUserInTop5;

  const renderPlayerRow = (player: LeaderboardItem, isCurrentRow?: boolean) => {
    const isCurrentUser = isCurrentRow || player.isCurrentUser;
    return (
      <div
        key={`${player.userId}-${player.rank}`}
        onClick={() => player.username && navigate(`/profile/${player.username}`)}
        className={`flex items-center py-2.5 transition-colors ${
          player.username ? 'cursor-pointer' : ''
        } ${
          isCurrentUser
            ? 'bg-[#FFF5EE] -mx-4 px-4 hover:bg-[#FFEEDB]'
            : 'hover:bg-gray-50/80'
        }`}
      >
        {/* Rank Number */}
        <div className="w-7 text-left pl-0.5 shrink-0">
          <span
            className={`text-sm ${
              isCurrentUser ? 'font-bold text-[#FC480D]' : 'font-semibold text-gray-400'
            }`}
          >
            {player.rank}
          </span>
        </div>

        {/* Avatar */}
        <div className="shrink-0">
          {player.avatar ? (
            <img
              src={player.avatar}
              alt={player.name}
              className="w-10 h-10 aspect-square rounded-full object-cover shrink-0"
            />
          ) : (
            <div
              className={`w-10 h-10 aspect-square rounded-full ${getAvatarBg(
                player.name,
                isCurrentUser
              )} text-white font-bold flex items-center justify-center text-xs shrink-0`}
            >
              {getInitials(player.name)}
            </div>
          )}
        </div>

        {/* Player Name, Position & Tier */}
        <div className="flex-1 min-w-0 pl-3 pr-2">
          <p className="text-sm font-bold text-gray-900 truncate">{player.name}</p>
          <div className="flex items-center gap-1.5 text-xs text-gray-400 font-medium mt-0.5">
            <Icon
              icon={getPositionIcon(player.position)}
              className="w-3.5 h-3.5 text-gray-400 shrink-0"
            />
            <span className="truncate">
              {player.position || 'Pemain'} · {player.tier || 'Unranked'}
            </span>
          </div>
        </div>

        {/* MMR Score */}
        <div className="text-right shrink-0">
          <p
            className={`text-sm leading-tight ${
              isCurrentUser ? 'font-extrabold text-[#FC480D]' : 'font-bold text-gray-900'
            }`}
          >
            {Math.round(player.mmr || 0).toLocaleString('id-ID')}
          </p>
          <span className="text-[10px] uppercase font-medium text-gray-400 tracking-wider">
            MMR
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-3xl pt-4 md:pt-5 border border-gray-100 shadow-[inset_0_0_0_1px_#E0E6ED] overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 px-4">
        <h2 className="text-base font-bold text-[#0E1726]">Papan Peringkat</h2>
        <button
          type="button"
          onClick={() => navigate('/mmr/leaderboard')}
          className="text-xs font-semibold text-[#FC480D] hover:underline cursor-pointer"
        >
          Lihat Semua
        </button>
      </div>

      {/* Position Filter Chips */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none px-4">
        {POSITION_FILTERS.map((pos) => {
          const isActive = selectedPosition === pos.id;
          return (
            <button
              key={pos.id || 'all'}
              type="button"
              onClick={() => setSelectedPosition(pos.id)}
              className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer shrink-0 ${
                isActive
                  ? 'border border-[#FC480D] text-[#FC480D] bg-[#FFF6F3]'
                  : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Icon
                icon={pos.icon}
                className={`w-3.5 h-3.5 ${isActive ? 'text-[#FC480D]' : 'text-gray-500'}`}
              />
              <span>{pos.label}</span>
            </button>
          );
        })}
      </div>

      {/* Content Area */}
      {isLoading ? (
        <div className="px-4 divide-y divide-gray-100 mt-1">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center py-2.5 animate-pulse">
              <div className="w-7 h-4 bg-gray-200 rounded shrink-0 mr-1" />
              <div className="w-10 h-10 rounded-full bg-gray-200 shrink-0" />
              <div className="flex-1 min-w-0 pl-3 pr-2 space-y-1.5">
                <div className="h-4 w-28 bg-gray-200 rounded" />
                <div className="h-3 w-20 bg-gray-100 rounded" />
              </div>
              <div className="w-14 space-y-1 text-right">
                <div className="h-4 w-12 bg-gray-200 rounded ml-auto" />
                <div className="h-2.5 w-6 bg-gray-100 rounded ml-auto" />
              </div>
            </div>
          ))}
        </div>
      ) : hasError ? (
        <div className="py-6 flex flex-col items-center justify-center text-center">
          <p className="text-xs text-muted-foreground">Gagal memuat papan peringkat</p>
          <button
            type="button"
            onClick={() => fetchLeaderboard(selectedPosition)}
            className="text-xs text-[#FC480D] font-semibold mt-1.5 hover:underline cursor-pointer"
          >
            Coba lagi
          </button>
        </div>
      ) : top5.length === 0 ? (
        <div className="py-8  flex flex-col items-center justify-center text-center">
          <div className="w-11 h-11 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 mb-2">
            <Icon icon="ph:trophy-bold" className="w-5 h-5 text-gray-400" />
          </div>
          <p className="text-xs font-semibold text-gray-700">Belum ada peringkat</p>
          <p className="text-[11px] text-gray-400 mt-0.5">Belum ada data pemain di posisi ini</p>
        </div>
      ) : (
        <div className="px-4 divide-y divide-gray-100 mt-1">
          {top5.map((player) =>
            renderPlayerRow(
              player,
              player.isCurrentUser || (currentUser && player.userId === currentUser.userId)
            )
          )}

          {/* Current User Row (shown when user is ranked and outside top 5) */}
          {showSeparateCurrentUser && renderPlayerRow(currentUser, true)}
        </div>
      )}
    </div>
  );
};

export default MMRLeaderboardCard;
